import { numberToPrettyString } from '@cian/utils';

import { isCommercialMultiAds } from './multiAds/isCommercialMultiAds';
import { ECoworkingOfferType, IOffer } from '../../../types/offer';

const COWORKING_WORKPLACE_TYPES: ReadonlySet<ECoworkingOfferType> = new Set([
  ECoworkingOfferType.Office,
  ECoworkingOfferType.FixedWorkplace,
  ECoworkingOfferType.FlexibleWorkplace,
]);

function isCoworkingWorkplacePrice(offer: IOffer): boolean {
  return Boolean(offer.coworkingOfferType && COWORKING_WORKPLACE_TYPES.has(offer.coworkingOfferType));
}

/** Минимальная цена среди частей мультиобъявления (для аренды — в месяц, для продажи — полная) */
function getMultiAdsMinPrice(offer: IOffer): number | undefined {
  const prices = (offer.areaParts ?? [])
    .map(part => part.price)
    .filter((price): price is number => typeof price === 'number' && price > 0);

  return prices.length > 0 ? Math.min(...prices) : undefined;
}

const SOTKAS_PER_HECTARE = 100;

function getLandAreaInPriceUnits(offer: IOffer, priceType: 'hectare' | 'sotka'): number | undefined {
  const area = Number(offer.land?.area);
  const areaUnitType = offer.land?.areaUnitType;

  if (!areaUnitType || !Number.isFinite(area) || area <= 0) {
    return undefined;
  }

  if (areaUnitType === priceType) {
    return area;
  }

  return priceType === 'sotka' ? area * SOTKAS_PER_HECTARE : area / SOTKAS_PER_HECTARE;
}

function getSalePrice(offer: IOffer): number {
  const { bargainTerms, minPriceTotalRur } = offer;
  const priceRur = bargainTerms?.priceRur;
  const priceType = bargainTerms?.priceType;

  if (priceRur) {
    if (priceType === 'squareMeter') {
      const totalArea = Number(offer.totalArea);
      if (Number.isFinite(totalArea) && totalArea > 0) {
        return Math.round(priceRur * totalArea);
      }
    }

    if (priceType === 'sotka' || priceType === 'hectare') {
      const landArea = getLandAreaInPriceUnits(offer, priceType);
      if (landArea) {
        return Math.round(priceRur * landArea);
      }
    }
  }

  return priceRur || minPriceTotalRur || 0;
}

export function getCommercialPriceMain(offer: IOffer): string {
  const { dealType, bargainTerms, minPriceTotalRur, minPriceTotalPerMonthRur, priceTotalPerMonthRur } = offer;

  if (dealType === 'rent') {
    if (isCoworkingWorkplacePrice(offer)) {
      const workplace = bargainTerms?.priceForWorkplace || priceTotalPerMonthRur || minPriceTotalPerMonthRur || 0;
      const pretext =
        !bargainTerms?.priceForWorkplace && !priceTotalPerMonthRur && minPriceTotalPerMonthRur ? 'от ' : '';

      return `${pretext}${numberToPrettyString(workplace)} ₽/мес. за рабочее место`;
    }

    // Для мультиобъявления показываем минимальную цену части с приставкой «от»
    if (isCommercialMultiAds(offer)) {
      const minMonthly = getMultiAdsMinPrice(offer) || minPriceTotalPerMonthRur;
      if (minMonthly) {
        return `от ${numberToPrettyString(minMonthly)} ₽/мес.`;
      }
    }

    const monthly = priceTotalPerMonthRur || minPriceTotalPerMonthRur || bargainTerms?.priceRur || 0;
    const pretext = minPriceTotalPerMonthRur && !priceTotalPerMonthRur ? 'от ' : '';

    return `${pretext}${numberToPrettyString(monthly)} ₽/мес.`;
  }

  if (isCommercialMultiAds(offer)) {
    const minSale = getMultiAdsMinPrice(offer) || minPriceTotalRur;
    if (minSale) {
      return `от ${numberToPrettyString(minSale)} ₽`;
    }
  }

  const sale = getSalePrice(offer);
  const pretext = minPriceTotalRur && !bargainTerms?.priceRur ? 'от ' : '';

  return `${pretext}${numberToPrettyString(sale)} ₽`;
}
